<template>
  <div>
    <ApiLoading v-if="isApiLoading" :apiError="apiLoading.error" />
    <!-- v-show is used so that the iFrame is still in DOM to be able to send/receive post messages -->
    <iframe v-show="!isApiLoading" :src="iframeUrl" width="100%" height="300" ref="iframe"></iframe>
  </div>
</template>

<script>
import { EnvironmentVariables } from '@general/js/environmentVariables';
import CommonCrmServices from '@crmCommon/services';
import { ApiLoading } from '@/components';

export default {
  name: 'IpsiCreditCardForm',

  components: {
    ApiLoading,
  },

  props: {
    isCRM: {
      type: Boolean,
      default: false,
    },
    /**
     * @description If true, iFrame is loaded with immediate payment only url params (short term token, card not saved). If false, iFrame is loaded with long term token url params (card saved).
     * @type {Boolean}
     * @default false
     */
    immediatePaymentOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      apiLoading: {
        loading: false,
        error: false,
      },
      isFormValid: false,
      userName: EnvironmentVariables.IPSI.USER_NAME,
      configId: this.immediatePaymentOnly ? EnvironmentVariables.IPSI.CONFIG_ID_IFRAME_2 : EnvironmentVariables.IPSI.CONFIG_ID_IFRAME_1,
      verifyMessage: this.immediatePaymentOnly ? EnvironmentVariables.IPSI.VERIFY_MESSAGE_IFRAME_2 : EnvironmentVariables.IPSI.VERIFY_MESSAGE_IFRAME_1,
      onlyTokenise: true, // hardcoded to true
      crmIframeUrl: null,
      _pendingSubmit: null,
    };
  },

  watch: {
    isFormValid(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('isCreditCardCompleted', newValue); // Emit event to parent component
      }
    },
    isApiLoading: {
      handler() {
        this.$emit('isApiLoading', this.apiLoading.loading); // Emit event to parent component
        this.$emit('isApiFailed', this.apiLoading.error); // Emit event to parent component
      },
    },
  },

  computed: {
    iframeUrl() {
      if (this.isCRM) {
        return this.crmIframeUrl;
      }
      if (this.immediatePaymentOnly) {
        return `${EnvironmentVariables.IPSI.BASE_URL}/es-client-frontend/embedded?tokenControl.tokenLifeTime=${EnvironmentVariables.IPSI.SHORT_TERM_TOKEN_TIMEOUT}&tokenControl.tokenUsageLimit=1&userName=${this.userName}&configId=${this.configId}&amount=1.00&onlyTokenise=${this.onlyTokenise}&verifyMessage=${this.verifyMessage}`;
      }
      return `${EnvironmentVariables.IPSI.BASE_URL}/es-client-frontend/embedded?userName=${this.userName}&configId=${this.configId}&amount=1.00&onlyTokenise=${this.onlyTokenise}&verifyMessage=${this.verifyMessage}`;
    },

    isApiLoading() {
      return this.apiLoading.loading || this.apiLoading.error;
    },

    origin() {
      return new URL(this.iframeUrl).origin; // Get the origin of the iframe URL
    },
  },

  async created() {
    if (this.isCRM) {
      try {
        this.apiLoading.loading = true; // Set loading to true when the iframe is loading.
        const res = await CommonCrmServices.getIpsiUrls();
        this.crmIframeUrl = this.immediatePaymentOnly ? res.shortTermTokenUrl : res.longTermTokenUrl;

        // Set up the message listener after crmIframeUrl is loaded
        this.setupMessageListener();
        this.emitSttTokenTimeout();
      } catch (error) {
        this.apiLoading.error = true;
        logger.log('Error fetching Ipsi config from CRM', error);
        this.$emit('fetchIpsiConfigCrmFail');
      }
    } else {
      // Set up the message listener immediately if not using CRM
      this.setupMessageListener();
      this.emitSttTokenTimeout();
    }
  },

  mounted() {
    this.apiLoading.loading = true; // Set loading to true when the iframe is loading.
  },

  // This hook only runs when this component is cached inside <keep-alive>
  activated() {
    this.resetForm(); // Reset form validity when the component is re-activated
  },

  methods: {
    emitSttTokenTimeout() {
      if (this.immediatePaymentOnly) {
        // short term token is only used for immediate payment flows
        try {
          const url = new URL(this.iframeUrl);
          const timeout = url.searchParams.get('tokenControl.tokenLifeTime') || EnvironmentVariables.IPSI.SHORT_TERM_TOKEN_TIMEOUT;
          this.$emit('sttTokenTimeout', timeout);
        } catch (error) {
          logger.log('Error parsing iframe URL', error);
          this.$emit('sttTokenTimeout', EnvironmentVariables.IPSI.SHORT_TERM_TOKEN_TIMEOUT);
        }
      }
    },

    setupMessageListener() {
      window.addEventListener('message', (event) => {
        if (event.origin !== this.origin) {
          return; // Ignore messages from other origins
        }

        this.validateForm(event.data);
        this.processIframeResponse(event.data);
      });
    },

    resetForm() {
      this.isFormValid = false; // Reset form validity
    },

    retryForm() {
      this.apiLoading.loading = false;
      this.apiLoading.error = false;
      this.isFormValid = false;
      this.$refs.iframe.src = this.iframeUrl; // Reload the iframe
    },

    validateForm(data) {
      try {
        const parsedData = JSON.parse(data);
        this.isFormValid = parsedData?.isValid ?? this.isFormValid;
      } catch (error) {
        logger.error('Error parsing postMessage data', error);
        this.isFormValid = false;
      }
    },

    processIframeResponse(data) {
      try {
        const parsedData = JSON.parse(data);
        let response = {
          sttLoadStatus: false,
        };

        if (parsedData.info === 'The payment form has rendered.') {
          this.apiLoading.loading = false; // Set loading to false when the iframe is loaded
          this.resetForm();
          return;
        }

        if (parsedData.info === 'Iframe session timeout.' || parsedData.info === 'Iframe failed.') {
          this.apiLoading.loading = false; // Set loading to false when the iframe is loaded
          this.apiLoading.error = true; // Set error message
          response = {
            sttLoadStatus: false,
            error: parsedData.info,
          };
          this.$emit('fetchCardToken', response);
          this.resetForm();
          this._resolvePendingSubmit(response);
          return;
        }

        if (parsedData.cardToken && parsedData.maskedPAN && parsedData.cardExpiryDate) {
          response = {
            sttLoadStatus: true,
            token: parsedData.cardToken,
            maskedPAN: parsedData.maskedPAN,
            cardExpiryDate: parsedData.cardExpiryDate,
            instrumentType: parsedData.instrumentType,
            cardCategory: parsedData.cardCategory,
            cardSubCategory: parsedData.cardSubCategory,
          };
          this.$emit('fetchCardToken', response);
          this._resolvePendingSubmit(response);
        }
        // All other messages (e.g. isValid updates) are only handled by validateForm —
        // do NOT resolve/reject the pending submit for those.
      } catch (error) {
        logger.error('Error parsing postMessage data', error);
      }
    },

    submit() {
      if (this.isFormValid) {
        this.$refs.iframe.contentWindow.postMessage(JSON.stringify({ action: 'submit' }), this.origin);
      }
    },

    // This method is used to submit the form and wait for the response from the iframe. It returns a promise that resolves with the response from the iframe.
    submitAwait() {
      this.apiLoading.loading = true;
      this.apiLoading.error = false;
      this.submit();
      return new Promise((resolve, reject) => {
        this._pendingSubmit = { resolve, reject };
      });
    },

    _resolvePendingSubmit(response) {
      if (!this._pendingSubmit) return;
      const { resolve, reject } = this._pendingSubmit;
      this._pendingSubmit = null;
      this.apiLoading.loading = false;
      if (response.sttLoadStatus) {
        resolve(response);
      } else {
        this.apiLoading.error = true;
        reject(new Error(response.error || 'Payment iFrame submission failed'));
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
